import { useState, useEffect } from "react";
import { submitSurvey } from "../api/services/surveyService";
import { CheckCircle, AlertCircle, Star, Heart, ArrowRight, Sparkles, Award, MessageCircle, User } from "lucide-react";

const SurveyPage = () => {
  const [responses, setResponses] = useState({
    pricing: null,
    waitTime: null,
    staffService: null,
    vehicleCondition: null,
    pickupLocation: null,
    overallExperience: null,
  });
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [missingQuestions, setMissingQuestions] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    reservationNumber: "",
    customerName: ""
  });

  // Extract URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const reservationNumber = urlParams.get('RN');
    const customerName = urlParams.get('Name');
    
    if (reservationNumber && customerName) {
      setCustomerInfo({
        reservationNumber: reservationNumber,
        customerName: customerName
      });
      
             // Check if feedback was already submitted for this reservation
       const submittedFeedbacks = JSON.parse(localStorage.getItem('submittedFeedbacks') || '[]');
       if (submittedFeedbacks.includes(reservationNumber)) {
         setFeedbackSubmitted(true);
         setApiResponse({
           success: false,
           message: "Feedback already submitted!"
         });
         return; // Exit early to prevent further processing
       }
    }
  }, []);

  const questions = [
    {
      id: "pricing",
      question: "How transparent were our pricing?",
      subtitle: "Were the prices clear and matched your expectations?",
      icon: "💳",
      options: [
        { value: 1, label: "Perfect transparency", description: "Everything matched what I expected", emoji: "😊", color: "emerald" },
        { value: 0, label: "Hidden fees found", description: "I experienced unexpected charges", emoji: "😟", color: "rose" },
      ],
    },
    {
      id: "waitTime",
      question: "How was your pickup experience?",
      subtitle: "Did you receive your car promptly?",
      icon: "⏰",
      options: [
        { value: 1, label: "Lightning fast", description: "Received my car without any delay", emoji: "⚡", color: "emerald" },
        { value: 0, label: "Too much waiting", description: "It took longer than expected", emoji: "😴", color: "rose" },
      ],
    },
    {
      id: "staffService",
      question: "How was our team's service?",
      subtitle: "Were our staff helpful and friendly?",
      icon: "👥",
      options: [
        { value: 1, label: "Outstanding service", description: "Friendly and incredibly helpful", emoji: "🌟", color: "emerald" },
        { value: 0, label: "Room for improvement", description: "Could have been more helpful", emoji: "😐", color: "rose" },
      ],
    },
    {
      id: "vehicleCondition",
      question: "How was your rental car?",
      subtitle: "Was the vehicle clean and well-maintained?",
      icon: "🚗",
      options: [
        { value: 1, label: "Spotless condition", description: "Clean and perfectly maintained", emoji: "✨", color: "emerald" },
        { value: 0, label: "Needs attention", description: "Could have been cleaner", emoji: "🧹", color: "rose" },
      ],
    },
    {
      id: "pickupLocation",
      question: "How convenient was our location?",
      subtitle: "Was it easy to find and access?",
      icon: "🗺️",
      options: [
        { value: 1, label: "Super convenient", description: "Easy to find and access", emoji: "📍", color: "emerald" },
        { value: 0, label: "Hard to locate", description: "Difficult to find or reach", emoji: "🤔", color: "rose" },
      ],
    },
    {
      id: "overallExperience",
      question: "Would you recommend us?",
      subtitle: "Would you suggest us to friends and family?",
      icon: "⭐",
      options: [
        { value: 1, label: "Absolutely recommend", description: "I would definitely suggest you", emoji: "💯", color: "emerald" },
        { value: 0, label: "Probably not", description: "I would not recommend", emoji: "😔", color: "rose" },
      ],
    },
  ];

  const progress = (Object.values(responses).filter(r => r !== null).length / questions.length) * 100;

  const scrollToQuestion = (questionId) => {
    const questionElement = document.getElementById(`question-${questionId}`);
    if (questionElement) {
      questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
    
    setTimeout(() => {
      const currentIndex = questions.findIndex(q => q.id === questionId);
      if (currentIndex < questions.length - 1) {
        setCurrentQuestion(currentIndex + 1);
      }
    }, 800);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if reservation number is present
    if (!customerInfo.reservationNumber) {
      setSubmitStatus("noReservation");
      setTimeout(() => setSubmitStatus(null), 4000);
      return;
    }
    
    const unansweredQuestions = questions.filter(q => responses[q.id] === null);
    // console.log('Current responses:', responses);
    // console.log('Unanswered questions:', unansweredQuestions);
    
    if (unansweredQuestions.length > 0) {
      setMissingQuestions(unansweredQuestions);
      setSubmitStatus("validation");
      
      // Scroll to the first unanswered question
      const firstUnansweredId = unansweredQuestions[0].id;
      const questionElement = document.getElementById(`question-${firstUnansweredId}`);
      if (questionElement) {
        questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      setTimeout(() => setSubmitStatus(null), 6000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

         try {
       const surveyData = {
         ...responses,
         comments: comments.trim(),
         reservation_number: customerInfo.reservationNumber,
       };

       const response = await submitSurvey(surveyData);
       setApiResponse(response);
       
       if (response.success) {
         // Store in localStorage to prevent resubmission
         const submittedFeedbacks = JSON.parse(localStorage.getItem('submittedFeedbacks') || '[]');
         submittedFeedbacks.push(customerInfo.reservationNumber);
         localStorage.setItem('submittedFeedbacks', JSON.stringify(submittedFeedbacks));
         
         setFeedbackSubmitted(true);
         setSubmitStatus("success");
       } else {
         // Handle API response with success: false (like "Survey already taken!")
         setFeedbackSubmitted(true);
         setSubmitStatus("alreadySubmitted");
       }
       
     } catch (error) {
       setSubmitStatus("error");
       console.error("Feedback submission failed:", error);
     } finally {
       setIsSubmitting(false);
     }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header - Only show if feedback not submitted */}
        {!feedbackSubmitted && (
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-18 sm:h-18 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-2xl sm:rounded-3xl mb-4 sm:mb-4 shadow-2xl">
              <Award className="w-6 h-6 sm:w-10 sm:h-10 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-slate-800 via-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3 sm:mb-4">
              Your Experience Matters
            </h1>
            <p className="text-sm sm:text-lg lg:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
              Help us create better car rental experiences by sharing your valuable feedback. 
              Your insights drive our improvements.
            </p>
          </div>
        )}

        {/* Customer Greeting - Only show if feedback not submitted */}
        {!feedbackSubmitted && customerInfo.customerName ? (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl sm:rounded-3xl p-4 sm:p-6 mb-6 sm:mb-10 shadow-xl">
                         <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
               <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 self-center sm:self-start">
                 <User className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
               </div>
               <div className="text-center sm:text-left flex-1">
                                 <h2 className="text-lg sm:text-xl lg:text-xl font-bold text-blue-800 mb-1 sm:mb-1">
                   Welcome back, {customerInfo.customerName}! <span className="inline-flex items-center">👋</span>
                 </h2>
                <p className="text-blue-700 text-sm sm:text-sm">
                  We're excited to hear about your experience with reservation <span className="font-mono font-semibold bg-blue-100 px-2 py-1 rounded">{customerInfo.reservationNumber}</span>
                </p>
              </div>
            </div>
          </div>
                 ) : !feedbackSubmitted ? (
           <div className="bg-amber-50/90 backdrop-blur-sm border-2 border-amber-200 rounded-xl sm:rounded-3xl p-4 sm:p-8 mb-6 sm:mb-12 shadow-xl">
             <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
               <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-amber-600 flex-shrink-0" />
               <div className="text-center sm:text-left">
                                  <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-amber-800 mb-1 sm:mb-2">
                   Feedback Access Required
                 </h2>
                                   <p className="text-amber-700 text-sm sm:text-lg">
                     This feedback form requires a valid reservation link. Please use the link provided in your booking confirmation.
                   </p>
               </div>
             </div>
           </div>
         ) : null}

                 {/* Show API Response if feedback was submitted */}
         {feedbackSubmitted && apiResponse && (
          <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 lg:p-10 border border-white/30 mb-6 sm:mb-12">
            <div className="text-center">
              {apiResponse.success ? (
                <div className="space-y-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-800 mb-2">
                    Thank You! 🎉
                  </h2>
                  <p className="text-lg sm:text-xl text-emerald-700 mb-4">
                    Your feedback has been submitted successfully.
                  </p>
                  <p className="text-base sm:text-lg text-slate-600">
                    We truly appreciate your time and valuable insights!
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                                     <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-amber-800 mb-2">
                     Feedback Already Submitted
                   </h2>
                  <p className="text-lg sm:text-xl text-amber-700 mb-4">
                    {apiResponse.message}
                  </p>
                  <p className="text-base sm:text-[18px] text-slate-600">
                    Thank you for your previous feedback!
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

                 {/* Feedback Content - Only show if not submitted */}
         {!feedbackSubmitted && (
          <>
            {/* Progress Indicator */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-3xl shadow-xl p-3 sm:p-4 mb-2 sm:mb-4 border border-white/20">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-2 space-y-2 sm:space-y-0">
                <div className="flex items-center space-x-2 sm:space-x-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <Sparkles className="w-2 h-2 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div>
                                    <h3 className="text-sm sm:text-lg font-semibold text-slate-800">Feedback Progress</h3>
                    <p className="text-xs sm:text-sm text-slate-500">Keep going, you're doing great!</p>
                  </div>
                </div>
                <div className="text-center sm:text-right">
                  <div className="text-lg sm:text-lg font-bold text-blue-600">{Math.round(progress)}%</div>
                  <div className="text-xs sm:text-sm text-slate-500">{Object.values(responses).filter(r => r !== null).length} of {questions.length}</div>
                </div>
            </div>
          
          <div className="relative">
            <div className="w-full bg-slate-200 rounded-full sm:h-4 overflow-hidden">
              <div 
                className="h-2 sm:h-4 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-600 rounded-full transition-all duration-1000 ease-out shadow-lg"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            {/* <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div> */}
          </div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-6">
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-4">
          {/* Questions */}
          {questions.map((question, index) => (
            <div 
                key={question.id} 
                id={`question-${question.id}`}
                className={`bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-6 border border-white/30 transition-all duration-500 transform ${
                  currentQuestion === index ? 'scale-105 shadow-3xl' : 'scale-100'
                } ${index < currentQuestion ? 'opacity-40' : 'opacity-100'} ${
                  missingQuestions.some(q => q.id === question.id) ? 'ring-4 ring-amber-400 animate-pulse' : ''
                }`}
              >
              <div className="text-center sm:text-left mb-4 sm:mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-1 sm:mb-2">
                  {question.question}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600">{question.subtitle}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {question.options.map((option) => (
                  <label
                      key={option.value}
                      className={`group relative flex items-center p-2 sm:p-2 border-2 rounded-lg sm:rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-80 ${
                        responses[question.id] === option.value
                          ? option.color === 'emerald' 
                            ? 'border-emerald-500 bg-gradient-to-r from-emerald-50 to-green-50 shadow-xl scale-80' 
                            : 'border-rose-500 bg-gradient-to-r from-rose-50 to-red-50 shadow-xl scale-80'
                          : 'border-slate-200 hover:border-slate-300 bg-white/50'
                      }`}
                    >
                    <input
                      type="radio"
                      name={question.id}
                      value={option.value}
                      checked={responses[question.id] === option.value}
                      onChange={() => handleResponseChange(question.id, option.value)}
                      className="sr-only"
                    />
                    
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-6 w-full">
                      <div className={`text-xl sm:text-xl transition-transform duration-300 group-hover:scale-110 flex justify-center sm:justify-start ${
                        responses[question.id] === option.value ? 'animate-bounce' : ''
                      }`}>
                        {option.emoji}
                      </div>
                      
                      <div className="flex-1 text-center sm:text-left">
                        <div className={`text-base sm:text-lg font-bold mb-1 sm:mb-2 ${
                          responses[question.id] === option.value 
                            ? option.color === 'emerald' ? 'text-emerald-700' : 'text-rose-700'
                            : 'text-slate-800'
                        }`}>
                          {option.label}
                        </div>
                        <div className="text-xs sm:text-xs text-slate-600">{option.description}</div>
                      </div>
                      
                      {responses[question.id] === option.value && (
                        <div className={`p-1 sm:p-3 rounded-full shadow-lg flex justify-center sm:justify-end ${
                          option.color === 'emerald' ? 'bg-emerald-500' : 'bg-rose-500'
                        } animate-pulse`}>
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}

          {/* Comments Section */}
          <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-3xl shadow-2xl p-4 sm:p-4 lg:p-4 border border-white/30">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-4">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-lg sm:rounded-2xl flex items-center justify-center">
                <MessageCircle className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-lg sm:text-xl font-bold text-slate-800">Share Your Thoughts</h3>
                <p className="text-xs sm:text-xs text-slate-600">Tell us more about your experience</p>
              </div>
            </div>
            
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="We'd love to hear more about your experience! Share any suggestions, feedback, or stories that could help us improve our service..."
              className="w-full h-20 sm:h-30 p-2 sm:p-4 border-2 border-slate-200 rounded-lg sm:rounded-2xl resize-none focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all duration-300 text-slate-700 placeholder-slate-400 bg-white/50 backdrop-blur-sm text-sm sm:text-sm"
            />
          </div>

          {/* Status Messages */}
          {submitStatus === "noReservation" && (
            <div className="bg-red-50/90 backdrop-blur-sm border-2 border-red-200 rounded-xl sm:rounded-3xl p-4 sm:p-8 animate-pulse">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-red-600 flex-shrink-0" />
                <div className="text-center sm:text-left">
                                     <p className="text-red-800 font-bold text-sm sm:text-lg">Reservation Number Required</p>
                   <p className="text-red-700 text-xs sm:text-base">Please access this feedback form using a valid reservation link.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === "validation" && (
            <div className="bg-amber-50/90 backdrop-blur-sm border-2 border-amber-200 rounded-xl sm:rounded-3xl p-4 sm:p-8 animate-pulse">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-amber-600 flex-shrink-0" />
                <div className="text-center sm:text-left">
                  <p className="text-amber-800 font-bold text-sm sm:text-lg">Please complete all questions</p>
                  <p className="text-amber-700 text-xs sm:text-base">
                    Missing: {missingQuestions.map(q => (
                      <button
                        key={q.id}
                        onClick={() => scrollToQuestion(q.id)}
                        className="text-amber-800 underline hover:text-amber-900 font-medium mx-1"
                      >
                        {q.question}
                      </button>
                    ))}
                  </p>
                  <p className="text-amber-600 text-xs mt-1">Click on any question above to jump to it</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === "success" && (
            <div className="bg-emerald-50/90 backdrop-blur-sm border-2 border-emerald-200 rounded-xl sm:rounded-3xl p-4 sm:p-8 animate-bounce">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-emerald-800 font-bold text-lg sm:text-2xl">Thank you! 🎉</p>
                  <p className="text-emerald-700 text-sm sm:text-base">Your feedback has been submitted successfully. We truly appreciate your time!</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="bg-rose-50/90 backdrop-blur-sm border-2 border-rose-200 rounded-xl sm:rounded-3xl p-4 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-rose-600 flex-shrink-0" />
                <div className="text-center sm:text-left">
                  <p className="text-rose-800 font-bold text-sm sm:text-lg">Oops! Something went wrong</p>
                  <p className="text-rose-700 text-xs sm:text-base">Please try submitting again. If the problem persists, please contact our support team.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === "alreadySubmitted" && (
            <div className="bg-amber-50/90 backdrop-blur-sm border-2 border-amber-200 rounded-xl sm:rounded-3xl p-4 sm:p-8 animate-pulse">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <AlertCircle className="w-5 h-5 sm:w-8 sm:h-8 text-amber-600 flex-shrink-0" />
                <div className="text-center sm:text-left">
                                     <p className="text-amber-800 font-bold text-sm sm:text-lg">Feedback Already Submitted</p>
                   <p className="text-amber-700 text-xs sm:text-base">This feedback has already been submitted for this reservation.</p>
                </div>
              </div>
            </div>
          )}

                     {/* Submit Button */}
           <div className="flex justify-center pt-4 sm:pt-4">
             <button
                  type="submit"
                  disabled={isSubmitting || !customerInfo.reservationNumber}
                  className={`group relative px-8 sm:px-12 py-3 sm:py-6 rounded-lg sm:rounded-2xl font-bold text-base sm:text-xl transition-all duration-300 transform hover:scale-105 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-2xl ${
                    progress === 100 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-emerald-500/50' 
                      : 'bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-600 text-white hover:shadow-blue-500/50'
                  }`}
                >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-4 h-4 sm:w-6 sm:h-6 border-2 sm:border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs sm:text-base">Submitting your feedback...</span>
                  </div>
                ) : !customerInfo.reservationNumber ? (
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <AlertCircle className="w-4 h-4 sm:w-6 sm:h-6" />
                    <span className="text-xs sm:text-base">Reservation Required</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Heart className="w-2 h-2 mr-2 sm:w-4 sm:h-4 group-hover:animate-pulse" />
                    <span className="text-xs sm:text-base">Submit Feedback</span>
                    <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
             </button>
           </div>
         </form>
         </div>
           </>
         )}
       </div>
     </div>
   );
 };

export default SurveyPage;
